import { SyntheticEvent, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  List,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  styled,
} from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import { useDispatch, useSelector } from 'react-redux'

import BasicTabs from 'components/tabs/basic-tabs/basic-tabs.component'
import TabPanel from 'components/tabs/tab-panel/tab-panel.component'
import TwoColumnLayout from 'layouts/two-column-layout/two-column-layout'
import InvoiceDaysButtons from 'components/invoice-days-buttons/invoice-days-buttons.component'
import { setDuration } from 'store/invoices/invoices.slice'
import {
  useListCarrierInvoicesByStatusQuery,
  useUpdateInvoiceStatusMutation,
} from 'store/invoices/invoices.api'
import {
  CarrierInvoice,
  CarrierPayableStatus,
  InvoiceType,
} from 'store/invoices/invoices.types'
import { RootState } from 'store'
import { StyledListItemButton } from 'pages/invoice/invoice-selection/invoice-list/invoice-list.styles'
import Plus from 'assets/icons/Plus Button.png'
import InvoiceDocumentsDialog from 'pages/invoice/invoice-details/invoice-table/invoice-table-body/invoice-table-documents/invoice-documents-dialog/invoice-documents-dialog.component'
import { DateTime } from 'luxon'
import { exportCarrierPayablesToCSV } from 'utils/csv-export'

import {
  carrierPayablesTableHeaders,
  carrierPayablesTabs,
} from './carrier-payables.constants'
import {
  LIST_TABLE_EVEN_ROW_BG,
  listPrimaryTextSx,
  listSecondaryTextSx,
  listTableBodyCellStyles,
  listTableHeaderCellStyles,
  listTableSx,
  listTableToolbarSx,
} from 'components/list-table/list-table.styles'

const HeaderTableCell = styled(TableCell)({
  ...listTableHeaderCellStyles,
  ...listTableBodyCellStyles,
})

const BodyTableCell = styled(TableCell)({
  ...listTableBodyCellStyles,
})

const CARRIER_COLUMN_WIDTHS: Record<string, number> = {
  load: 14,
  order: 12,
  carrier: 16,
  aging: 9,
  amount: 11,
  documents: 14,
  action: 24,
}

const Triangle = styled('span')<{ direction: 'up' | 'down'; active?: boolean }>(
  ({ direction, active, theme }) => ({
    marginLeft: 6,
    width: 0,
    height: 0,
    borderLeft: '6px solid transparent',
    borderRight: '6px solid transparent',
    ...(direction === 'down'
      ? {
          borderTop: `10px solid ${
            active ? theme.palette.text.primary : theme.palette.text.disabled
          }`,
        }
      : {
          borderBottom: `10px solid ${
            active ? theme.palette.text.primary : theme.palette.text.disabled
          }`,
        }),
  })
)

const tabStatusMap: CarrierPayableStatus[] = [
  'TO_DELIVER',
  'TO_BE_INVOICED',
  'NOT_PAID',
  'PAID',
]

const tabToInvoiceWorkspaceType = (tabsValue: number): InvoiceType => {
  if (tabsValue === 0) return 'TO_DELIVER'
  if (tabsValue === 1) return 'TO_INVOICE'
  if (tabsValue === 2) return 'NOT_PAID'
  return 'PAID'
}

const formatAmount = (amount: number, currency: string) => {
  const value = Number.isFinite(amount) ? amount : 0
  return `${currency || 'CAD'} ${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const getCarrierAgingDays = (invoice: CarrierInvoice): number => {
  if (!invoice.dueDate) return 0
  const due = DateTime.fromISO(invoice.dueDate).startOf('day')
  if (!due.isValid) return 0
  return Math.max(
    0,
    Math.round(DateTime.now().startOf('day').diff(due, 'days').days)
  )
}

const CarrierDocumentsCell = ({
  orderId,
  invoiceId,
  invoiceType,
}: {
  orderId: string
  invoiceId: string
  invoiceType: InvoiceType
}) => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <img
          src={Plus}
          alt="plus"
          width={34}
          height={34}
          style={{ cursor: 'pointer' }}
          onClick={() => setOpen(true)}
        />
        <Button
          sx={{
            height: '36px',
            borderRadius: '6px',
            textTransform: 'capitalize',
          }}
          variant="contained"
          onClick={() => setOpen(true)}
        >
          View
        </Button>
      </Stack>
      <InvoiceDocumentsDialog
        orderId={orderId}
        invoiceId={invoiceId}
        invoiceType={invoiceType}
        variant="carrier"
        open={open}
        handleClose={() => setOpen(false)}
      />
    </>
  )
}

const CarrierPayablesPage = () => {
  const dispatch = useDispatch()
  const duration = useSelector((state: RootState) => state.invoices.duration)
  const [tabsValue, setTabsValue] = useState(0)
  const [listSearch, setListSearch] = useState('')
  const [tableSearch, setTableSearch] = useState('')
  const [selectedCarrier, setSelectedCarrier] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState(
    'Please select at least one invoice to export.'
  )

  const isToPayTab = tabsValue === 2
  const status = tabStatusMap[tabsValue]
  const workspaceInvoiceType = tabToInvoiceWorkspaceType(tabsValue)

  const {
    data: invoices = [],
    isLoading,
    isFetching,
  } = useListCarrierInvoicesByStatusQuery({ status })

  const [updateInvoiceStatus] = useUpdateInvoiceStatusMutation()

  const carriers = useMemo(() => {
    const names = Array.from(
      new Set(
        invoices
          .map((invoice) => invoice.carrierName?.trim())
          .filter((name): name is string => Boolean(name))
      )
    ).sort((a, b) => a.localeCompare(b))
    return names
  }, [invoices])

  const filteredCarriers = useMemo(() => {
    const query = listSearch.trim().toLowerCase()
    if (!query) return carriers
    return carriers.filter((name) => name.toLowerCase().includes(query))
  }, [carriers, listSearch])

  const filteredInvoices = useMemo(() => {
    let rows = invoices
    if (selectedCarrier) {
      rows = rows.filter((invoice) => invoice.carrierName === selectedCarrier)
    }
    if (isToPayTab && duration !== 'All') {
      rows = rows.filter((invoice) => {
        const aging = getCarrierAgingDays(invoice)
        switch (duration) {
          case 'Under30':
            return aging < 30
          case 'Over30':
            return aging >= 30 && aging < 45
          case 'Over45':
            return aging >= 45 && aging < 60
          case 'Over60':
            return aging >= 60 && aging < 90
          case 'Over90':
            return aging >= 90
          default:
            return true
        }
      })
    }
    const query = tableSearch.trim().toLowerCase()
    if (!query) return rows
    return rows.filter((invoice) => {
      const haystack = [
        invoice.loadNumber,
        invoice.sourceUserOrderId,
        invoice.carrierName,
        invoice.orderId,
        formatAmount(invoice.totalAmount, invoice.currency),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(query)
    })
  }, [invoices, selectedCarrier, tableSearch, isToPayTab, duration])

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabsValue(newValue)
    setListSearch('')
    setTableSearch('')
    setSelectedCarrier(null)
    setSelectedIds(new Set())
    dispatch(setDuration('All'))
  }

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const allSelected =
    filteredInvoices.length > 0 &&
    filteredInvoices.every((invoice) => selectedIds.has(invoice.id))

  const someSelected =
    selectedIds.size > 0 &&
    filteredInvoices.some((invoice) => selectedIds.has(invoice.id)) &&
    !allSelected

  const totalInvoiceAmount = useMemo(() => {
    const rowsToSum =
      selectedIds.size > 0
        ? filteredInvoices.filter((invoice) => selectedIds.has(invoice.id))
        : filteredInvoices
    return rowsToSum.reduce((acc, invoice) => acc + (invoice.totalAmount ?? 0), 0)
  }, [filteredInvoices, selectedIds])

  const currency =
    filteredInvoices[0]?.currency || invoices[0]?.currency || 'CAD'

  const handleExportCSV = () => {
    if (selectedIds.size === 0) {
      setErrorMessage('Please select at least one invoice to export.')
      setErrorOpen(true)
      return
    }
    exportCarrierPayablesToCSV(invoices, selectedIds)
  }

  const handleStatusUpdate = async (
    invoice: CarrierInvoice,
    nextStatus: CarrierPayableStatus
  ) => {
    setUpdatingId(invoice.id)
    try {
      await updateInvoiceStatus({ id: invoice.id, status: nextStatus }).unwrap()
    } finally {
      setUpdatingId(null)
    }
  }

  const showActionColumn = tabsValue === 0 || tabsValue === 2 || tabsValue === 3

  const carrierColumnWidth = (headerId: string) => {
    const visibleHeaders = carrierPayablesTableHeaders.filter(
      (item) =>
        !(item.id === 'aging' && !isToPayTab) &&
        !(item.id === 'action' && !showActionColumn)
    )
    const weightTotal = visibleHeaders.reduce(
      (sum, item) => sum + (CARRIER_COLUMN_WIDTHS[item.id] || 10),
      0
    )
    const weight = CARRIER_COLUMN_WIDTHS[headerId] || 10
    return `${((weight / weightTotal) * 100).toFixed(2)}%`
  }

  const selectionPanel = (
    <BasicTabs
      tabs={carrierPayablesTabs}
      value={tabsValue}
      handleChange={handleTabChange}
      scrollableContent
    >
      {carrierPayablesTabs.map((tab, index) => (
        <TabPanel key={tab.id} value={tabsValue} index={index}>
          <Box mt={2}>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 8,
                bgcolor: 'background.paper',
                pb: 1,
              }}
            >
              {index === 2 && <InvoiceDaysButtons />}
              <TextField
                type="text"
                placeholder="Search Carrier"
                variant="outlined"
                size="small"
                value={listSearch}
                onChange={(event) => setListSearch(event.target.value)}
                sx={{ width: '100%', marginTop: index === 2 ? 2 : 0 }}
              />
            </Box>
            <List>
              <StyledListItemButton
                selected={selectedCarrier === null}
                onClick={() => setSelectedCarrier(null)}
              >
                <ListItemText>All</ListItemText>
              </StyledListItemButton>
              {filteredCarriers.map((carrier) => (
                <StyledListItemButton
                  key={carrier}
                  selected={selectedCarrier === carrier}
                  onClick={() => setSelectedCarrier(carrier)}
                >
                  <ListItemText>{carrier}</ListItemText>
                </StyledListItemButton>
              ))}
            </List>
          </Box>
        </TabPanel>
      ))}
    </BasicTabs>
  )

  const detailsPanel = (
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        maxHeight: '100%',
        overflow: 'hidden',
        justifyContent: 'space-between',
      }}
    >
      <Stack spacing={2} sx={listTableToolbarSx}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <TextField
            placeholder="Search"
            variant="outlined"
            size="small"
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            sx={{ minWidth: 200, flexShrink: 0 }}
          />
        </Box>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 0,
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            overscrollBehaviorY: 'contain',
          }}
        >
          <Table
            aria-label="carrier payables table"
            stickyHeader
            sx={listTableSx}
          >
            <TableHead>
              <TableRow>
                {carrierPayablesTableHeaders.map((header, index) => {
                  if (header.id === 'aging' && !isToPayTab) {
                    return null
                  }
                  if (header.id === 'action' && !showActionColumn) {
                    return null
                  }

                  const sortable =
                    header.id === 'load' ||
                    header.id === 'order' ||
                    header.id === 'carrier' ||
                    header.id === 'aging' ||
                    header.id === 'amount'

                  return (
                    <HeaderTableCell
                      key={header.id}
                      sx={{ width: carrierColumnWidth(header.id) }}
                      align="left"
                    >
                      {index === 0 ? (
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          justifyContent="flex-start"
                          columnGap={1}
                        >
                          <Checkbox
                            sx={{ padding: '4px' }}
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(
                                  new Set(
                                    filteredInvoices.map((invoice) => invoice.id)
                                  )
                                )
                              } else {
                                setSelectedIds(new Set())
                              }
                            }}
                          />
                          <Box display="flex" alignItems="center">
                            <Box component="span">{header.label}</Box>
                            <Triangle direction="down" />
                          </Box>
                        </Box>
                      ) : sortable ? (
                        <Box display="flex" alignItems="center">
                          <Box component="span">{header.label}</Box>
                          <Triangle direction="down" />
                        </Box>
                      ) : (
                        header.label
                      )}
                    </HeaderTableCell>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || isFetching ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={28} sx={{ my: 4 }} />
                  </TableCell>
                </TableRow>
              ) : filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" sx={{ py: 6 }}>
                      No carrier payables found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((invoice, rowIndex) => (
                  <TableRow
                    key={invoice.id}
                    hover
                    sx={{
                      ...(rowIndex % 2 === 1
                        ? { bgcolor: LIST_TABLE_EVEN_ROW_BG }
                        : null),
                    }}
                  >
                    <BodyTableCell>
                      <Box display="flex" alignItems="center" columnGap={1}>
                        <Checkbox
                          sx={{ padding: '4px' }}
                          checked={selectedIds.has(invoice.id)}
                          onChange={(e) =>
                            toggleSelected(invoice.id, e.target.checked)
                          }
                        />
                        <Typography sx={listPrimaryTextSx} noWrap>
                          {invoice.loadNumber || '—'}
                        </Typography>
                      </Box>
                    </BodyTableCell>
                    <BodyTableCell>
                      <Typography sx={listPrimaryTextSx} noWrap>
                        {invoice.sourceUserOrderId || '—'}
                      </Typography>
                    </BodyTableCell>
                    <BodyTableCell>
                      <Typography sx={listPrimaryTextSx} noWrap>
                        {invoice.carrierName || '—'}
                      </Typography>
                    </BodyTableCell>
                    {isToPayTab && (
                      <BodyTableCell>
                        <Typography sx={listPrimaryTextSx}>
                          {getCarrierAgingDays(invoice)}
                        </Typography>
                        <Typography sx={listSecondaryTextSx}>Days</Typography>
                      </BodyTableCell>
                    )}
                    <BodyTableCell>
                      <Typography sx={listPrimaryTextSx} noWrap>
                        {formatAmount(invoice.totalAmount, invoice.currency)}
                      </Typography>
                    </BodyTableCell>
                    <BodyTableCell>
                      <CarrierDocumentsCell
                        orderId={invoice.orderId}
                        invoiceId={invoice.id}
                        invoiceType={workspaceInvoiceType}
                      />
                    </BodyTableCell>
                    {showActionColumn && (
                      <BodyTableCell>
                        {tabsValue === 0 && (
                          <Button
                            variant="contained"
                            sx={{
                              height: '36px',
                              borderRadius: '6px',
                              textTransform: 'capitalize',
                              whiteSpace: 'nowrap',
                            }}
                            disabled={updatingId === invoice.id}
                            onClick={() =>
                              handleStatusUpdate(invoice, 'TO_BE_INVOICED')
                            }
                          >
                            Mark as delivered
                          </Button>
                        )}
                        {tabsValue === 2 && (
                          <Button
                            variant="contained"
                            sx={{
                              height: '36px',
                              borderRadius: '6px',
                              textTransform: 'capitalize',
                              whiteSpace: 'nowrap',
                            }}
                            disabled={updatingId === invoice.id}
                            onClick={() => handleStatusUpdate(invoice, 'PAID')}
                          >
                            Mark as Paid
                          </Button>
                        )}
                        {tabsValue === 3 && (
                          <Button
                            variant="contained"
                            sx={{
                              height: '36px',
                              borderRadius: '6px',
                              textTransform: 'capitalize',
                              whiteSpace: 'nowrap',
                            }}
                            disabled={updatingId === invoice.id}
                            onClick={() =>
                              handleStatusUpdate(invoice, 'NOT_PAID')
                            }
                          >
                            Mark as Unpaid
                          </Button>
                        )}
                      </BodyTableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Total Invoice Amount: {currency} {totalInvoiceAmount.toFixed(2)}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handleExportCSV}
          >
            Export
          </Button>
        </Box>
      </Box>
    </Stack>
  )

  return (
    <>
      <TwoColumnLayout>
        {selectionPanel}
        {detailsPanel}
        {selectionPanel}
      </TwoColumnLayout>
      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorOpen(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default CarrierPayablesPage
